import { Button, Flex, Tabs, colors } from '@aloft/toolkit-sui';

import { useState } from 'react';
import { FormProvider } from 'react-hook-form';

import { useZodForm } from '../../hooks/useZodForm.ts';
import { setSettings } from '../../redux/features/actions/setSettings.ts';
import {
  selectIncludeCostApproach,
  selectSettingsFormValues,
} from '../../redux/features/selectors/orderSelectors.ts';
import { backfillDefaultFeatureAreas } from '../../redux/features/thunks/backfillDefaultFeatureAreas.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { DefaultCraftsmanQuality } from './DefaultCraftsmanQuality.tsx';
import { DefaultFeatureAreas } from './DefaultFeatureAreas.tsx';
import {
  CompGridSettingsFormInput,
  CompGridSettingsFormOutput,
  schCompGridSettingsForm,
} from './schSettingsForm.ts';

const compGridSettingsModalStyles = `
  .comp-grid-settings-modal .simple-input.outline {
    padding-left: 12px;
    padding-right: 12px;
  }
`;

const tabs = {
  area: 'Area',
  craftsmanQuality: 'Craftsman Quality',
};

type Tab = keyof typeof tabs;

type Props = {
  modalOnClose: () => void;
};

function definedEntries<T extends Record<string, number | undefined>>(
  record: T | undefined,
): Partial<Record<keyof T, number>> | undefined {
  if (!record) return undefined;
  const entries = Object.entries(record).filter(
    (entry): entry is [string, number] => typeof entry[1] === 'number',
  );
  return entries.length > 0
    ? (Object.fromEntries(entries) as Partial<Record<keyof T, number>>)
    : {};
}

function compGridSettingsPayload(
  data: CompGridSettingsFormOutput,
  includeCostApproach: boolean,
  saveAsDefault?: boolean,
) {
  return setSettings({
    defaultFeatureAreas: data.defaultFeatureAreas,
    showFeatureAreasOnGrid: includeCostApproach ? data.showFeatureAreasOnGrid : false,
    uadToCraftsmanQuality: definedEntries(data.uadToCraftsmanQuality),
    showCraftsmanQualityOnGrid: includeCostApproach ? data.showCraftsmanQualityOnGrid : false,
    ...(saveAsDefault ? { saveAsDefault: true } : {}),
  });
}

export const CompGridSettingsModalContent = ({ modalOnClose }: Props) => {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<Tab>('area');
  const settingsFormValues = useAppSelector(selectSettingsFormValues);
  const includeCostApproach = useAppSelector(selectIncludeCostApproach);
  const defaultValues: CompGridSettingsFormInput = {
    defaultFeatureAreas: settingsFormValues.defaultFeatureAreas,
    showFeatureAreasOnGrid: includeCostApproach
      ? settingsFormValues.showFeatureAreasOnGrid
      : 'false',
    uadToCraftsmanQuality: settingsFormValues.uadToCraftsmanQuality,
    showCraftsmanQualityOnGrid: includeCostApproach
      ? settingsFormValues.showCraftsmanQualityOnGrid
      : 'false',
  };

  const settingsForm = useZodForm({
    defaultValues,
    schema: schCompGridSettingsForm,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  function onApply(data: CompGridSettingsFormOutput): void {
    dispatch(compGridSettingsPayload(data, includeCostApproach, true));
    dispatch(backfillDefaultFeatureAreas());
    modalOnClose();
  }

  return (
    <FormProvider {...settingsForm}>
      <div className="comp-grid-settings-modal">
        <style>{compGridSettingsModalStyles}</style>
        <Tabs
          variant="secondary"
          tabs={Object.entries(tabs).map(([value, label]) => ({ value, label }))}
          activeTab={activeTab}
          setActiveTab={(tab: string) => {
            setActiveTab(tab as Tab);
          }}
        />
        <Flex padding="1.5rem" flexDirection="column">
          {activeTab === 'area' ? <DefaultFeatureAreas /> : <DefaultCraftsmanQuality />}
        </Flex>
        <Flex justifyContent="flex-end" gap="1rem" paddingX="1.5rem" paddingBottom="1.5rem">
          <Button
            variant="tertiary"
            text="Close"
            onClick={modalOnClose}
            __dangerDoNotUseSx={{ color: colors.textPrimary }}
          />
          <Button
            variant="primary"
            text="Apply"
            onClick={settingsForm.handleSubmit(onApply)}
            __dangerDoNotUseSx={{ paddingInline: '20px', minWidth: '80px' }}
          />
        </Flex>
      </div>
    </FormProvider>
  );
};
